// /home/john/Desktop/workspace/eurydice/view/app/src/persistence/LocalFSAdapter.ts

export type FSNode = FSDirectoryNode | FSFileNode;

export interface FSDirectoryNode {
    kind: 'directory';
    name: string;
    path: string; // relative to root
    children: FSNode[];
}

export interface FSFileNode {
    kind: 'file';
    name: string;
    path: string; // relative to root
    size?: number;
    lastModified?: number;
}

export type ReadType = 'text' | 'arrayBuffer' | 'blob' | 'json';

export class LocalFSAdapter {
    private root: FileSystemDirectoryHandle | null = null;

    constructor(root?: FileSystemDirectoryHandle) {
        if (root) this.root = root;
    }

    static isSupported(): boolean {
        return typeof window !== 'undefined' && !!(window as any).showDirectoryPicker;
    }

    // Optional convenience method to let the user pick a directory
    static async pickDirectory(options?: {id, mode, startIn}): Promise<FileSystemDirectoryHandle> {
        if (!LocalFSAdapter.isSupported()) throw new Error('File System Access API not supported.');
        return await (window as any).showDirectoryPicker?.(options);
    }

    setRoot(handle: FileSystemDirectoryHandle) {
        this.root = handle;
    }

    getRoot(): FileSystemDirectoryHandle {
        if (!this.root) throw new Error('Root directory handle not set.');
        return this.root;
    }

    async ensurePermission(mode: 'read' | 'readwrite' = 'read', handle?: FileSystemHandle): Promise<boolean> {
        const h = (handle ?? this.root) as any;
        if (!h) throw new Error('No handle provided and root is not set.');
        if (typeof h.queryPermission !== 'function') return true;

        const opts = { mode };
        let perm: PermissionState = await h.queryPermission(opts);
        if (perm === 'granted') return true;
        if (perm === 'denied') return false;
        perm = await h.requestPermission(opts);
        return perm === 'granted';
    }

    async listDirectory(path = '', includeMetadata = false): Promise<FSNode[]> {
        const dir = await this.resolveDirectory(path, false);
        const nodes: FSNode[] = [];
        for await (const [name, handle] of (dir as any).entries()) {
            const relPath = this.joinPath(path, name);
            if (handle.kind === 'directory') {
                nodes.push({ kind: 'directory', name, path: relPath, children: [] });
            } else {
                if (includeMetadata) {
                    const file = await handle.getFile();
                    nodes.push({
                        kind: 'file',
                        name,
                        path: relPath,
                        size: file.size,
                        lastModified: file.lastModified,
                    });
                } else {
                    nodes.push({ kind: 'file', name, path: relPath });
                }
            }
        }
        nodes.sort((a, b) => {
            if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        return nodes;
    }

    async listTree(maxDepth = Infinity, includeMetadata = false): Promise<FSDirectoryNode> {
        const root = this.getRoot();
        await this.ensurePermission('read', root);
        return await this.walkDirectory(root, '', 0, maxDepth, includeMetadata);
    }

    async readFile(path: string, type: ReadType = 'text'): Promise<string | ArrayBuffer | Blob | unknown> {
        const fileHandle = await this.getFileHandle(path, false);
        await this.ensurePermission('read', fileHandle);
        const file = await fileHandle.getFile();
        switch (type) {
            case 'text':
                return await file.text();
            case 'arrayBuffer':
                return await file.arrayBuffer();
            case 'blob':
                return file;
            case 'json': {
                const txt = await file.text();
                return JSON.parse(txt);
            }
        }
    }

    async writeFile(
        path: string,
        data: string | Blob | ArrayBuffer | Uint8Array | DataView,
    ): Promise<void> {
        const fileHandle = await this.getFileHandle(path, true);
        const ok = await this.ensurePermission('readwrite', fileHandle);
        if (!ok) throw new Error('Write permission denied.');
        const writable = await (fileHandle as any).createWritable();
        try {
            await writable.write(this.asWritableData(data));
        } finally {
            await writable.close();
        }
    }

    async exists(path: string): Promise<'file' | 'directory' | false> {
        const { dirPath, base } = this.splitPath(path);
        let dir: FileSystemDirectoryHandle;
        try {
            dir = await this.resolveDirectory(dirPath, false);
        } catch {
            return false;
        }
        // Try file
        try {
            await dir.getFileHandle(base, { create: false });
            return 'file';
        } catch {
            // Try directory
            try {
                await dir.getDirectoryHandle(base, { create: false });
                return 'directory';
            } catch {
                return false;
            }
        }
    }

    async delete(path: string, options: { recursive?: boolean } = {}): Promise<void> {
        const { dirPath, base } = this.splitPath(path);
        const dir = await this.resolveDirectory(dirPath, false);
        const ok = await this.ensurePermission('readwrite', dir);
        if (!ok) throw new Error('Delete permission denied.');
        await (dir as any).removeEntry(base, { recursive: !!options.recursive });
    }

    // Helpers

    private async walkDirectory(
        dir: FileSystemDirectoryHandle,
        basePath: string,
        depth: number,
        maxDepth: number,
        includeMetadata: boolean,
    ): Promise<FSDirectoryNode> {
        const node: FSDirectoryNode = {
            kind: 'directory',
            name: basePath ? basePath.split('/').pop() || '' : '',
            path: basePath,
            children: [],
        };
        for await (const [name, handle] of (dir as any).entries()) {
            const childPath = this.joinPath(basePath, name);
            if (handle.kind === 'directory') {
                if (depth < maxDepth) {
                    const child = await this.walkDirectory(handle, childPath, depth + 1, maxDepth, includeMetadata);
                    node.children.push(child);
                } else {
                    node.children.push({ kind: 'directory', name, path: childPath, children: [] });
                }
            } else {
                if (includeMetadata) {
                    const file = await handle.getFile();
                    node.children.push({
                        kind: 'file',
                        name,
                        path: childPath,
                        size: file.size,
                        lastModified: file.lastModified,
                    });
                } else {
                    node.children.push({ kind: 'file', name, path: childPath });
                }
            }
        }
        node.children.sort((a, b) => {
            if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        return node;
    }

    private async resolveDirectory(path: string, create: boolean): Promise<FileSystemDirectoryHandle> {
        const root = this.getRoot();
        await this.ensurePermission(create ? 'readwrite' : 'read', root);
        const segments = this.normalizePath(path);
        let dir: FileSystemDirectoryHandle = root;
        for (const seg of segments) {
            dir = await dir.getDirectoryHandle(seg, { create });
        }
        return dir;
    }

    private async getFileHandle(path: string, create: boolean): Promise<FileSystemFileHandle> {
        const { dirPath, base } = this.splitPath(path);
        const dir = await this.resolveDirectory(dirPath, create);
        return await dir.getFileHandle(base, { create });
    }

    private normalizePath(path: string): string[] {
        const p = (path || '').trim().replace(/\\/g, '/');
        if (!p) return [];
        const parts = p.split('/').filter(Boolean);
        for (const seg of parts) {
            if (seg === '.' || seg === '') continue;
            if (seg === '..') throw new Error('Parent path ".." is not allowed.');
        }
        return parts;
    }

    private splitPath(path: string): { dirPath: string; base: string } {
        const parts = this.normalizePath(path);
        if (parts.length === 0) throw new Error('Path must include a filename.');
        const base = parts.pop() as string;
        const dirPath = parts.join('/');
        return { dirPath, base };
    }

    private joinPath(a: string, b: string): string {
        if (!a) return b;
        if (!b) return a;
        return `${a.replace(/\/+$/, '')}/${b.replace(/^\/+/, '')}`;
    }

    private asWritableData(input: string | Blob | ArrayBuffer | Uint8Array | DataView): string | Blob | BufferSource | Uint8Array | ArrayBufferLike {
        if (typeof input === 'string' || input instanceof Blob) return input;
        if (input instanceof ArrayBuffer) return input;
        if (input instanceof DataView) return input.buffer;
        // Uint8Array or other typed arrays
        return input;
    }
}